﻿<script setup lang="ts">
    import { inject, onMounted, reactive, watch } from 'vue';
    import List from '@shared/components/List.vue';
    import SubscriptionListItem from './SubscriptionListItem.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { debounce } from '@shared/utils';
    import { SubscriptionListAccess } from './SubscriptionList.access';
    import { useRoute, useRouter } from 'vue-router';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionListAccess: SubscriptionListAccess = injectorAccess.injector.get(SubscriptionListAccess);
    const route = useRoute();
    const router = useRouter();

    onMounted(async () => {
        const searchParam = route.query.s as string;
        if (searchParam) {
            subscriptionListAccess.state.freeTextFilter = searchParam;
        }

        if (subscriptionListAccess.state.serviceSubscriptions == null) {
            await subscriptionListAccess.getList().then(() => {
                focusSearchInput();
            });
        }

        focusSearchInput();
    });

    watch(
        () => subscriptionListAccess.state.freeTextFilter,
        (newVal, oldVal) => {
            if (oldVal === undefined) return;

            debounce(
                async () => {
                    router.replace({ query: { ...route.query, s: newVal } });
                    await subscriptionListAccess.freeTextFilterChanged();
                },
                { delay: 300 }
            );
        },
        { immediate: true }
    );

    const orderOptions = () => {
        return [
            { display: $Resources.CreatedLabel, key: 'Created' },
            { display: $Resources.NameLabel, key: 'ClientCompanyName' },
            { display: 'Service level', key: 'TierContactLimit' },
        ];
    };

    function focusSearchInput(): void {
        setTimeout(() => {
            document.getElementById('subscription-list-search')?.focus();
        }, 50);
    }
</script>

<template>
    <List
        itemId="ServiceSubscriptionId"
        title="Subscriptions"
        :allLoaded="subscriptionListAccess.state.allItemsLoaded"
        :totalItemCount="subscriptionListAccess.state.serviceSubscriptionsCount"
        :perPage="30"
        :items="subscriptionListAccess.state.serviceSubscriptions"
        :loading="subscriptionListAccess.state.loading"
        :orderOptions="orderOptions()"
        :serverSideFilter="true"
        :hideSearch="true"
        @loadMore="subscriptionListAccess.loadMoreItems">
        <template #search>
            <BSFormSearch
                v-model="subscriptionListAccess.state.freeTextFilter"
                style="min-width: 400px"
                inputId="subscription-list-search"
                :disabled="false"
                @reset="() => (subscriptionListAccess.state.freeTextFilter = '')"></BSFormSearch>
        </template>
        <template #toolbar>
            <button class="btn btn-default" @click="subscriptionListAccess.getList(true)">Refresh</button>
        </template>
        <template #item="{ item, index }">
            <SubscriptionListItem :subscription="item" :index="index"></SubscriptionListItem>
        </template>
    </List>
</template>

<style></style>
